import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchAvalSchedule,
  markAvalRowManualApi,
  regenerateAvalSchedule,
  unmarkAvalRowManualApi,
  type AvalScheduleResponse,
} from "../api";

/**
 * Cuadro de comisiones FUTURAS de un aval concreto. Muestra la fila de
 * apertura (si openingRatePercent > 0) y las cuotas periódicas hasta la
 * fecha de posible vencimiento (o hasta 31/12/2028 si no la hay).
 *
 * El cuadro se regenera con el botón "Regenerar cuadro" — la regeneración
 * preserva las filas ya pagadas (por (type, index)) para no perder trazas
 * de contabilizaciones futuras.
 */

function fmtMoney(n: number, currency: string): string {
  return n.toLocaleString("es-ES", { style: "currency", currency, minimumFractionDigits: 2 });
}

function fmtDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

export function AvalSchedule() {
  const params = useParams<{ bankCode: string; lineNumber: string; avalNumber: string }>();
  const bankCode = params.bankCode ?? "";
  const lineNumber = params.lineNumber ?? "";
  const avalNumber = params.avalNumber ?? "";

  const [data, setData] = useState<AvalScheduleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setError(null);
    try {
      const r = await fetchAvalSchedule(bankCode, lineNumber, avalNumber);
      setData(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCode, lineNumber, avalNumber]);

  const regen = async () => {
    setBusy(true);
    try {
      await regenerateAvalSchedule(bankCode, lineNumber, avalNumber);
      await load();
    } catch (e: unknown) {
      alert(`Error al regenerar: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const totals = useMemo(() => {
    if (!data) return { opening: 0, periodic: 0, all: 0 };
    let opening = 0;
    let periodic = 0;
    for (const r of data.rows) {
      if (r.type === "opening") opening += r.commission;
      else periodic += r.commission;
    }
    return { opening, periodic, all: opening + periodic };
  }, [data]);

  if (error) {
    return (
      <section className="card">
        <p className="error">Error: {error}</p>
        <p>
          <Link to={`/avales/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}`}>
            ← Volver a la línea
          </Link>
        </p>
      </section>
    );
  }
  if (!data) {
    return <section className="card"><p>Cargando…</p></section>;
  }

  const a = data.aval;
  const currency = a.currency || "EUR";

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>
            <Link to="/avales" style={{ fontWeight: 400, opacity: 0.7 }}>Avales</Link>
            {" · "}
            <Link
              to={`/avales/${encodeURIComponent(bankCode)}/${encodeURIComponent(lineNumber)}`}
              style={{ fontWeight: 400, opacity: 0.7 }}
            >
              Línea {lineNumber}
            </Link>
            {" · Aval "}
            {avalNumber}
          </h2>
          <p className="meta">
            Nominal {fmtMoney(a.amount, currency)} · Constitución{" "}
            {a.constitutionDate ? fmtDate(a.constitutionDate) : "sin fecha"} · Vencim.{" "}
            {a.possibleMaturityDate ? fmtDate(a.possibleMaturityDate) : `(hasta ${fmtDate(data.defaultHorizon)})`} ·{" "}
            Estado <b>{a.status}</b>
            {" · "}Apertura {a.openingRatePercent ?? 0}% · Anual {a.commissionRateAnnual ?? 0}% · Periodic. {a.periodicity}
            {a.commissionMinimum ? ` · Mín ${fmtMoney(a.commissionMinimum, currency)}` : ""}
          </p>
          {a.recipient && (
            <p className="meta">
              Receptor: {a.recipient}{a.recipientCif ? ` (${a.recipientCif})` : ""}
              {a.reason ? ` · ${a.reason}` : ""}
              {(a.costingCode || a.projectCode) ? ` · CECO ${a.costingCode || "—"} · Proy. ${a.projectCode || "—"}` : ""}
            </p>
          )}
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" onClick={regen} disabled={busy || !a.constitutionDate || a.status !== "Vigente"}>
            {busy ? "Regenerando…" : "Regenerar cuadro"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {a.status !== "Vigente" && (
        <p className="warning">
          El aval está en estado "{a.status}". No se generan comisiones futuras.
        </p>
      )}
      {a.status === "Vigente" && !a.constitutionDate && (
        <p className="warning">
          Falta la fecha de constitución. Añádela en el detalle de la línea antes de generar el cuadro.
        </p>
      )}

      {data.rows.length === 0 && (
        data.generatedAt === null ? (
          <p className="meta">
            Aún no se ha generado el cuadro. Pulsa "Regenerar cuadro".
          </p>
        ) : (
          <p className="warning">
            El cuadro se generó pero quedó vacío. Este aval no tiene ni % de comisión anual
            ni comisión mínima definidos (ambos a 0 o vacíos), así que no hay nada que
            devengar. Edita el aval y rellena al menos uno de los dos, luego pulsa
            "Regenerar cuadro".
          </p>
        )
      )}

      {data.rows.length > 0 && (
        <>
          <div style={{ display: "flex", gap: "1.5rem", flexWrap: "wrap", padding: "0.5rem 0" }}>
            <div><b>Apertura:</b> {fmtMoney(totals.opening, currency)}</div>
            <div><b>Cuotas periódicas:</b> {fmtMoney(totals.periodic, currency)}</div>
            <div><b>Total previsto:</b> {fmtMoney(totals.all, currency)}</div>
            {data.horizonUsed && <div className="meta">Horizonte: {fmtDate(data.horizonUsed)}</div>}
            {data.generatedAt && <div className="meta">Generado: {fmtDate(data.generatedAt.slice(0, 10))}</div>}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th style={{ textAlign: "right" }}>Base</th>
                  <th style={{ textAlign: "right" }}>% aplicado</th>
                  <th style={{ textAlign: "right" }}>Comisión</th>
                  <th>Pagada</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const onToggle = async () => {
                    try {
                      if (r.paidAt && r.manuallyMarked) {
                        await unmarkAvalRowManualApi(bankCode, lineNumber, avalNumber, r.type, r.index);
                      } else if (!r.paidAt) {
                        const today = new Date().toISOString().slice(0, 10);
                        const paidAt = prompt(
                          "Fecha de contabilización manual (YYYY-MM-DD):",
                          r.dueDate?.slice(0, 10) || today,
                        );
                        if (!paidAt || !/^\d{4}-\d{2}-\d{2}$/.test(paidAt)) return;
                        await markAvalRowManualApi(bankCode, lineNumber, avalNumber, r.type, r.index, paidAt);
                      }
                      await load();
                    } catch (e: unknown) {
                      alert(e instanceof Error ? e.message : String(e));
                    }
                  };
                  return (
                    <tr
                      key={`${r.type}__${r.index}`}
                      style={r.type === "opening" ? { background: "rgba(240, 200, 100, 0.10)" } : undefined}
                    >
                      <td>{r.index}</td>
                      <td>{r.type === "opening" ? "Apertura" : "Periódica"}</td>
                      <td>{fmtDate(r.dueDate)}</td>
                      <td style={{ textAlign: "right" }}>{fmtMoney(r.basisAmount, currency)}</td>
                      <td style={{ textAlign: "right" }}>{r.ratePercent.toFixed(4)}%</td>
                      <td style={{ textAlign: "right" }}>{fmtMoney(r.commission, currency)}</td>
                      <td>
                        {r.paidAt ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
                            <span
                              className="badge ok"
                              title={r.paidJdtNum ? `JdtNum ${r.paidJdtNum}` : r.manuallyMarked ? "Marcada manualmente (implantación)" : "pagada"}
                            >
                              ✓ {fmtDate(r.paidAt.slice(0, 10))}
                              {r.manuallyMarked ? " (man.)" : ""}
                            </span>
                            {r.manuallyMarked && !r.paidJdtNum && (
                              <button
                                type="button"
                                onClick={onToggle}
                                style={{ fontSize: "0.7em", padding: "1px 6px" }}
                              >
                                Desmarcar
                              </button>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={onToggle}
                            style={{ fontSize: "0.75em", padding: "2px 8px" }}
                            title="Marcar como contabilizado manualmente (fase de implantación)"
                          >
                            Marcar
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
